import { CodeScannerStandard, CodeScannerStandards } from "../../code-scanner-standards";
import { SerializedComponent } from "../../components/SerializedApp";
import { getFeatureSetting } from "../../feature-settings";
import { panic } from "../../Support";
import { setColumnInRowForEditComponent } from "../builder-utils";
import { ComponentBuilder, ComponentConfiguratorContext } from "../components";
import { ColumnPropertyFlag, ColumnPropertyHandler, SwitchPropertyHandler } from "../components/property-handlers";
import { PropertySection } from "../components/property-sections";
import {
    ActionDescription,
    ActionKind,
    InputOutputTables,
    MutatingScreenKind,
    PropertyDescription,
    getPrimitiveNonHiddenColumns,
} from "../description";
import { ActionDescriptor } from "./action-descriptor";
import { DescriptionToken } from "./action-handler";
import { BaseActionHandler, tokenForProperty } from "./base";

interface ScanCodeConfigDescription {
    readonly useExtendedEAN: PropertyDescription;
    readonly scanForQrCode: PropertyDescription;
}

interface ScanCodeActionDescription extends ActionDescription, ScanCodeConfigDescription {
    readonly output: PropertyDescription;
}

const columnPropertyHandler = new ColumnPropertyHandler(
    "output",
    "Column",
    [ColumnPropertyFlag.Editable, ColumnPropertyFlag.Required, ColumnPropertyFlag.EditedInApp],
    undefined,
    ["qrcode", "barcode", "sku"],
    getPrimitiveNonHiddenColumns,
    "string",
    PropertySection.Data
);

const extendedEANHandler = new SwitchPropertyHandler(
    { useExtendedEAN: false },
    "Scan for EAN extensions",
    PropertySection.Data
);

const qrScanHandler = new SwitchPropertyHandler({ scanForQrCode: false }, "Scan for QR Codes", PropertySection.Data);

const featureHandlers = [extendedEANHandler, qrScanHandler];

function getStandardsForConfig(desc: ScanCodeActionDescription): CodeScannerStandards {
    const standards = [CodeScannerStandard.EAN_13, CodeScannerStandard.EAN_8, CodeScannerStandard.UPC_E];
    if (extendedEANHandler.getSwitch(desc)) {
        standards.push(CodeScannerStandard.EAN_5, CodeScannerStandard.EAN_2);
    }
    standards.push(
        CodeScannerStandard.Code39,
        CodeScannerStandard.Code39_VIN,
        CodeScannerStandard.Code128,
        CodeScannerStandard.i2of5
    );
    if (qrScanHandler.getSwitch(desc)) {
        standards.push(CodeScannerStandard.QR);
    }
    return standards;
}

export class ScanCodeActionHandler extends BaseActionHandler<ScanCodeActionDescription> {
    public readonly kind = ActionKind.ScanCode;

    public readonly iconName = "09-06-barcode-scan";
    public get handlesSequencing(): boolean {
        return true;
    }

    public getDescriptor(): ActionDescriptor {
        return {
            name: "Scan Barcode or QR Code",
            needsScreenContext: true,
            isLegacy: !getFeatureSetting("scanCodeAction"),
            properties: [columnPropertyHandler, ...featureHandlers],
        };
    }

    public makeAction(
        builder: ComponentBuilder,
        desc: ScanCodeActionDescription,
        tables: InputOutputTables,
        screenContext: SerializedComponent | undefined,
        outputContext: SerializedComponent,
        mutatingScreenKind: MutatingScreenKind | undefined,
        _detailScreenTitle: SerializedComponent | undefined,
        _itemID: SerializedComponent | undefined,
        onSuccess: SerializedComponent | undefined
    ): SerializedComponent | undefined {
        const { b } = builder;
        if (screenContext === undefined) {
            return panic("Must have screen context for Scan Code action");
        }

        const outputProperty = columnPropertyHandler.getColumnName(desc);
        if (outputProperty === undefined) return undefined;

        const setColumnsResult = setColumnInRowForEditComponent(
            b,
            mutatingScreenKind,
            outputProperty,
            b.getProperty(outputProperty, outputContext, tables.output),
            undefined,
            tables.output,
            undefined,
            screenContext,
            { debounce: false, withDiode: false }
        );
        if (setColumnsResult === undefined) return undefined;
        const { lets, valueToModify, action, isEnabled } = setColumnsResult;

        const actionSequence: SerializedComponent[] = [];
        if (action !== undefined) {
            actionSequence.push(action);
        }
        if (onSuccess !== undefined) {
            actionSequence.push(onSuccess);
        }
        actionSequence.push(b.popModalScreen);

        return b.let(
            lets,
            b.if(
                isEnabled ?? b.constant(true),
                builder.makePushCodeScannerScreen(
                    b.constant(getStandardsForConfig(desc)),
                    b.constant(mutatingScreenKind !== undefined),
                    valueToModify,
                    b.sequence(actionSequence)
                )
            )
        );
    }

    public getTokenizedDescription(
        desc: ScanCodeActionDescription,
        tables: InputOutputTables,
        ccc: ComponentConfiguratorContext
    ): readonly DescriptionToken[] | undefined {
        const token = tokenForProperty(desc.output, tables, ccc);
        if (token === undefined) return undefined;
        return [{ kind: "string", value: "Scan into" }, token];
    }
}
